import React, {
  memo, useMemo, useState, useEffect
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import {
  PortalStyles, Text, Icon
} from 'components/atoms';
import { ModuleActions, ResponsiveViewer } from 'components/molecules';
import { useEditorContext, actions } from 'context/EditorContext';
import { useDispatch } from 'react-redux'
import { saveDraft } from 'redux/draftsSlice'
import { isHiddenModule } from 'modules/utils';
import modules from 'modules';
import { useRouterContext } from 'hooks';
import { screenType } from 'types';
import { generateEditorPath } from 'services/generators';
import EditorModule from 'views/Editor/EditorModule';
import { produce } from 'immer'
import { toast } from 'react-toastify';
import { Toast } from 'containers'

const PageRenderer = memo(({
  page, lang, moduleToFocus, screen, isPreview,
}) => (
  <PortalStyles>
    <ResponsiveViewer screen={screen}>
      <Container>
        {page
            && page.map((section, index) => {
              if (!section) return null
              const { name, ...props } = section
              if (isHiddenModule(name)) return null;

              const key = `${name}-${index}`;
              const shouldFocus = moduleToFocus === key;
              return (
                <Section
                  name={name}
                  index={index}
                  key={key}
                  lang={lang}
                  shouldFocus={shouldFocus}
                  section={section}
                  isPreview={isPreview}
                  screen={screen}
                  original={page}
                  {...props}
                />
              );
            })}
      </Container>
    </ResponsiveViewer>
  </PortalStyles>
));

PageRenderer.propTypes = {
  lang: PropTypes.string,
  page: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
    })
  ),
  moduleToFocus: PropTypes.string,
  screen: screenType,
  isPreview: PropTypes.bool,
};

PageRenderer.defaultProps = {
  lang: 'pt',
  page: [],
  moduleToFocus: null,
  screen: 'desktop',
  isPreview: false,
};

// renders a component based of the base components object
const Section = ({
  name, index, shouldFocus, section, isPreview, screen, original, ...props
}) => {
  const key = useMemo(() => `${name}-${index}`, [name, index]);
  const hasModule = useMemo(() => {
    if (modules[name] && modules[name].module) return true;
    return false;
  }, [name]);

  const hasEditor = useMemo(() => {
    if (modules[name] && modules[name].editor) return true;
    return false;
  }, [name]);

  const [{ pageLiveData, sectionActive, moduleBeingEdited }, dispatch] = useEditorContext();
  const dispatchRedux = useDispatch()
  const {
    history,
    match: {
      params: { pageId, lang, subweb },
    },
  } = useRouterContext();

  const [isActive, setActive] = useState(sectionActive === key);
  const [touched, setTouched] = useState(false);

  const editorHomePath = useMemo(
    () => generateEditorPath({ lang, pageId, subweb }
    ), [lang, pageId, subweb])

  const Component = useMemo(
    () => {
      if (!hasModule) return modules.Placeholder.module
      if (moduleBeingEdited === key && modules[name].options.isInline) return EditorModule
      return modules[name].module
    },
    [hasModule, name, moduleBeingEdited, key]
  );

  useEffect(() => {
    setTouched(!!sectionActive);
    setActive(key === sectionActive);
  }, [sectionActive, key]);

  const setActiveSection = (e, section) => {
    dispatch({
      type: actions.SECTION_SET_ACTIVE,
      data: section,
    });
  };

  const setInactiveSection = (e, section) => {
    dispatch({
      type: actions.SECTION_SET_INACTIVE,
      data: section,
    });
  };

  const openModuleEditor = key => {
    dispatch({
      type: actions.SET_MODULE_EDITED,
      data: {
        name,
        key
      },
    });
    const path = generateEditorPath({
      subweb, lang, pageId, moduleId: key
    });
    history.push(path);
  };

  const closeModuleEditor = () => {
    dispatch({
      type: actions.SET_MODULE_EDITED,
      data: {
        key: null,
        name: null,
      },
    });
    history.push(editorHomePath)
  }

  const deleteModule = moduleIndex => {
    const pageWithoutModule = produce(pageLiveData, draftState => {
      // eslint-disable-next-line no-param-reassign
      draftState.splice(moduleIndex, 1)
    })
    dispatch({
      type: actions.PAGE_DATA_UPDATE,
      data: pageWithoutModule,
    });
    dispatchRedux(saveDraft({ pageId, content: pageWithoutModule, lang }))
    history.push(editorHomePath)
    toast.success(
      <Toast
        type="success"
        title="Módulo removido"
        description={(
          <Text>
            Para desfazer as alterações clique no icon
            {' '}
            <Icon icon="fas fa-undo" height={10} />
            {' '}
            em cima.
          </Text>
        )}
      />
    );
  };

  const moveModuleUp = ({ currentIndex, section }) => {
    // ignore if first or after SEO
    if (currentIndex === 0 || currentIndex === 1) return;
    const destinationIndex = currentIndex - 1
    const pageReordered = produce(pageLiveData, draftState => {
      draftState.splice(currentIndex, 1)
      draftState.splice(destinationIndex, 0, section)
    })
    dispatch({
      type: actions.PAGE_DATA_UPDATE,
      data: pageReordered,
    });
    dispatchRedux(saveDraft({ pageId, content: pageReordered, lang }))
    history.push(editorHomePath)
  }

  const moveModuleDown = ({ currentIndex, section }) => {
    // ignore if last
    if (currentIndex === pageLiveData.length - 1) return;
    const destinationIndex = currentIndex + 1
    const pageReordered = produce(pageLiveData, draftState => {
      draftState.splice(currentIndex, 1)
      draftState.splice(destinationIndex, 0, section)
    })
    dispatch({
      type: actions.PAGE_DATA_UPDATE,
      data: pageReordered,
    });
    dispatchRedux(saveDraft({ pageId, content: pageReordered, lang }))
    history.push(editorHomePath)
  }

  const onCopy = index => {
    const copied = pageLiveData[index];
    window.sessionStorage.setItem('module2paste', JSON.stringify(copied));
  }
  // handle module focus here
  useEffect(() => {
    if (shouldFocus) {
      setActiveSection(null, key);
      return;
    }
    setInactiveSection(null, key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldFocus, key]);


  const handleMouseOver = e => {
    setActiveSection(e, key);
  };

  const handleMouseLeave = e => {
    setInactiveSection(e, key);
  };

  const isFirst = useMemo(() => index === 0 || index === 1, [index])
  const isLast = useMemo(() => index === pageLiveData?.length - 1, [index, pageLiveData])

  if (isPreview) {
    return (
      <Component {...props} name={name} hasEditor={hasEditor} pageOriginal={original} />
    )
  }

  return (
    <SectionWrapper
      onMouseOver={e => handleMouseOver(e)}
      onFocus={e => handleMouseOver(e)}
      onMouseLeave={e => handleMouseLeave(e)}
      onEdit={() => openModuleEditor(key)}
      onDelete={() => deleteModule(index)}
      onMoveUp={() => moveModuleUp({ currentIndex: index, section })}
      onMoveDown={() => moveModuleDown({ currentIndex: index, section })}
      onCloseEdit={closeModuleEditor}
      isEditing={moduleBeingEdited === key}
      isActive={isActive}
      touched={touched}
      name={key}
      isFirst={isFirst}
      isLast={isLast}
      sectionActive={sectionActive}
      onCopy={() => onCopy(index)}
    >
      <Component {...props} isMobilePreview={screen === 'mobile'} name={name} hasEditor={hasEditor} pageOriginal={original} />
    </SectionWrapper>
  );
};

Section.propTypes = {
  name: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  shouldFocus: PropTypes.bool,
  section: PropTypes.shape({}).isRequired,
  isPreview: PropTypes.bool,
  original: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
    })
  ),
};

Section.defaultProps = {
  shouldFocus: false,
  isPreview: false,
  original: []
};

// Add editor specific interactions to each section here
const SectionWrapper = ({
  children,
  onMouseOver,
  onMouseLeave,
  isActive,
  touched,
  onEdit,
  onCloseEdit,
  isEditing,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  sectionActive,
  onCopy,
}) => (
  <Wrapper
    onMouseOver={onMouseOver}
    onFocus={onMouseOver}
    onMouseLeave={onMouseLeave}
    isActive={isActive}
    touched={touched}
  >
    <ActiveOverlay
      isActive={isActive}
      isEditing={isEditing}
      onClick={isEditing ? () => { } : onEdit}
    />
    {(isActive || isEditing) && (
      <SectionActions>
        <ModuleActions
          onEdit={onEdit}
          onCloseEdit={onCloseEdit}
          isEditing={isEditing}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          isFirst={isFirst}
          isLast={isLast}
          sectionActive={sectionActive}
          onCopy={onCopy}
        />
      </SectionActions>
    )}
    {children}
  </Wrapper>
)

SectionWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  onMouseOver: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onCloseEdit: PropTypes.func,
  isActive: PropTypes.bool,
  touched: PropTypes.bool,
  isEditing: PropTypes.bool,
  onMoveUp: PropTypes.func,
  onMoveDown: PropTypes.func,
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  sectionActive: PropTypes.string,
  onCopy: PropTypes.func,
};

SectionWrapper.defaultProps = {
  isActive: false,
  touched: false,
  onMouseLeave: () => null,
  onMouseOver: () => null,
  onEdit: () => null,
  onDelete: () => null,
  onCloseEdit: () => null,
  isEditing: false,
  onMoveUp: () => { },
  onMoveDown: () => { },
  isFirst: false,
  isLast: false,
  sectionActive: '',
  onCopy: () => null,
};

const Wrapper = styled.div`
  position: relative;
  z-index: ${props => (props.isActive ? props.theme.values.zIndex.m : props.theme.values.zIndex.s)};
`;

const SectionActions = styled.div`
  position: sticky;
  right: 0;
  top: 50px;
  z-index: ${props => props.theme.values.zIndex.superBig};
`;

const ActiveOverlay = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  border: 2px solid ${props => {
    if (props.isEditing) return props.theme.colors.primaryDarker
    if (props.isActive) return props.theme.colors.primaryLighter
    return 'rgba(0,0,0,0)'
  }};
  z-index: ${props => props.theme.values.zIndex.superBig};
  transition: all 0.2s ease-in-out;
  pointer-events: ${props => (props.isEditing ? 'none' : 'initial')};
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

export default PageRenderer;
